import { isNullish } from '@breezy/shared'
import { retryExchange } from '@urql/exchange-retry'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AnyVariables,
  CombinedError,
  cacheExchange,
  createClient,
  type Exchange,
  fetchExchange,
  makeResult,
  mapExchange,
  Operation,
  type OperationResult,
  Provider,
} from 'urql'
import { filter, fromValue, map, merge, mergeMap, pipe, share } from 'wonka'
import { useErrorModal } from '../components/ErrorModal/ErrorModal'
import { FullScreenLoadingSpinner } from '../components/LoadingSpinner/LoadingSpinner'
import { getConfig } from '../config'
import { trpc } from '../hooks/trpc'
import { useAuth } from '../hooks/useAuth'
import { useMessage } from '../utils/antd-utils'
import { createGraphqlErrorAlertMessage, postErrorAlert } from '../utils/GraphqlAlerts'
import { getUrqlOperationName, toGraphQlError } from '../utils/GraphqlErrorMessages'
import { rumError } from '../utils/rum'
import { ConsecutiveFailureTracker, failureLimitExchange } from '../utils/UrqlFailureLimitExchange'
import { metricsExchange } from '../utils/UrqlMetricsExchange'
import { useUrqlSubscriptionExchange } from '../utils/UrqlSubscriptionExchange'
import { type ConnectivityStatus, useOptionalConnectivity } from './ConnectivityProvider'
import { type NetworkToastApi, useNetworkToast } from './NetworkToastProvider'

type UrqlErrorPolicy = 'modal' | 'silent' | 'toast'

type UrqlErrorContext = {
  errorPolicy?: UrqlErrorPolicy
  dedupeKey?: string
  dedupeTtlMs?: number
}

type DedupeEntry = {
  firstShownAt: number
  ttlMs: number
}

const DEFAULT_URQL_ERROR_POLICY: UrqlErrorPolicy = 'toast'
const DEFAULT_URQL_ERROR_TOAST_TTL_MS = 10_000
const DEFAULT_URQL_ERROR_TOAST_DURATION_SECONDS = 10

const isUrqlErrorContext = (ctx: unknown): ctx is UrqlErrorContext => {
  if (!ctx || typeof ctx !== 'object') return false
  const candidate = ctx as Record<string, unknown>
  const { errorPolicy, dedupeKey, dedupeTtlMs } = candidate

  const policyIsValid =
    errorPolicy === undefined || errorPolicy === 'modal' || errorPolicy === 'silent' || errorPolicy === 'toast'
  const dedupeKeyIsValid = dedupeKey === undefined || typeof dedupeKey === 'string'
  const ttlIsValid = dedupeTtlMs === undefined || typeof dedupeTtlMs === 'number'

  return policyIsValid && dedupeKeyIsValid && ttlIsValid
}

const useGraphQLAccessToken = () => {
  const [graphQLAccessToken, setGraphQLAccessToken] = useState('')
  const auth = useAuth()

  const trpcContext = trpc.useContext()

  useEffect(() => {
    // if there is no auth user, there is no point in trying to fetch the principal user
    if (!auth.user) {
      return
    }

    // if we already have the principal user, no need to fetch it again
    if (graphQLAccessToken) {
      return
    }
    trpcContext.client.user['users:self-and-graphql-access-token']
      .query()
      .then(({ graphqlAccessToken }) => {
        setGraphQLAccessToken(graphqlAccessToken)
      })
      .catch(e => {
        // if there is an error fetching, logout and have the user sign in
        console.error(`There was an error fetching users:self-and-graphql-access-token`, e)
        auth.logout()
        auth.loginWithRedirect()
      })
  }, [auth, trpcContext, graphQLAccessToken])

  return graphQLAccessToken
}

const isErrorLikelyRelatedToQueryPermissionConfiguration = (stringErr: string): boolean =>
  stringErr.includes('field') && stringErr.includes('not found in type')

const createOfflineCacheExchange = (
  lastResults: Map<number, OperationResult>,
  getConnectivityStatus: () => ConnectivityStatus,
): Exchange => {
  return ({ forward }) => {
    return ops$ => {
      const sharedOps$ = pipe(ops$, share)
      const isOfflineQuery = (op: Operation) =>
        op.kind === 'query' && (navigator.onLine === false || getConnectivityStatus() === 'offline')

      // Only short-circuit queries when truly offline; let mutations/subscriptions flow so failures surface.
      const offlineOps$ = pipe(
        sharedOps$,
        filter((op: Operation) => isOfflineQuery(op)),
      )
      const onlineOps$ = pipe(
        sharedOps$,
        filter((op: Operation) => !isOfflineQuery(op)),
      )

      const offlineResults$ = pipe(
        offlineOps$,
        mergeMap((operation: Operation) => {
          const cached = lastResults.get(operation.key)
          if (cached?.data) {
            return fromValue({
              ...cached,
              operation,
              stale: true,
            })
          }

          return fromValue({
            ...makeResult(operation, { data: null }),
            stale: true,
          })
        }),
      )

      const onlineResults$ = forward(onlineOps$)

      return pipe(
        merge([offlineResults$, onlineResults$]),
        map(result => {
          if (result.data) {
            lastResults.set(result.operation.key, result)
            return result
          }

          if (result.error?.networkError) {
            const cached = lastResults.get(result.operation.key)
            if (cached?.data) {
              return {
                ...result,
                data: cached.data,
                error: undefined,
                stale: true,
              }
            }
          }

          return result
        }),
      )
    }
  }
}

const useHandleGraphQLError = (networkToast: NetworkToastApi, getConnectivityStatus: () => ConnectivityStatus) => {
  const errorModal = useErrorModal()
  const message = useMessage()
  const dedupeRef = useRef<Map<string, DedupeEntry>>(new Map())

  const pruneExpired = useCallback((now: number) => {
    const dedupeMap = dedupeRef.current
    for (const [key, entry] of dedupeMap.entries()) {
      if (now - entry.firstShownAt >= entry.ttlMs) {
        dedupeMap.delete(key)
      }
    }
  }, [])

  return useCallback(
    async (error: CombinedError, operation: Operation<unknown, AnyVariables>) => {
      const ctx = isUrqlErrorContext(operation.context) ? operation.context : undefined
      const policy = ctx?.errorPolicy ?? DEFAULT_URQL_ERROR_POLICY
      if (policy === 'silent') return

      if (error.graphQLErrors.length > 0) {
        const graphQlError = error.graphQLErrors[0]
        const stringErr = JSON.stringify(graphQlError)
        const { errorTitle, errorMessage } = toGraphQlError(graphQlError, operation)
        if (policy === 'modal') {
          errorModal.pushError({
            title: errorTitle,
            content: errorMessage,
          })
        } else {
          const now = Date.now()
          pruneExpired(now)

          const defaultKey = `urql-error/${getUrqlOperationName(operation) ?? 'unknown'}`
          const dedupeKey = ctx?.dedupeKey ?? defaultKey
          const ttlMs = ctx?.dedupeTtlMs ?? DEFAULT_URQL_ERROR_TOAST_TTL_MS

          const existing = dedupeRef.current.get(dedupeKey)
          const firstShownAt = existing?.firstShownAt ?? now
          if (!existing) {
            dedupeRef.current.set(dedupeKey, { firstShownAt: now, ttlMs })
          }

          const remainingMs = ttlMs - (now - firstShownAt)
          if (remainingMs > 0) {
            const durationSeconds = Math.min(DEFAULT_URQL_ERROR_TOAST_DURATION_SECONDS, remainingMs / 1000)
            message.open({
              key: dedupeKey,
              type: 'error',
              content: `${errorTitle}: ${errorMessage}`,
              duration: durationSeconds,
            })
          }
        }

        if (isErrorLikelyRelatedToQueryPermissionConfiguration(stringErr)) {
          console.error('Graphql error, likely related to Query Permission Configuration', graphQlError)
        } else {
          console.error('Graphql error', graphQlError)
        }

        rumError('urql_graphql_error', error, {
          operationKind: operation.kind,
          operationName: getUrqlOperationName(operation),
          isSubscription: operation.kind === 'subscription',
        })
        await postErrorAlert(createGraphqlErrorAlertMessage(error, operation))
      }

      if (error.networkError) {
        if (operation.kind === 'subscription') {
          rumError('urql_network_error', error.networkError, {
            operationKind: operation.kind,
            operationName: getUrqlOperationName(operation),
            isSubscription: true,
          })
          return
        }

        networkToast.showNetworkToast({
          source: 'urql',
          error: error.networkError,
          status: getConnectivityStatus(),
        })
        console.error('Network error', error.networkError)
        return
      }
    },
    [errorModal, getConnectivityStatus, message, networkToast, pruneExpired],
  )
}

const useUrqlClient = (graphQLAccessToken?: string) => {
  const connectivity = useOptionalConnectivity()
  const networkToast = useNetworkToast()
  const statusRef = useRef<ConnectivityStatus>(connectivity?.status ?? 'healthy')
  const lastQueryResultsRef = useRef<Map<number, OperationResult>>(new Map())

  useEffect(() => {
    if (connectivity?.status) {
      statusRef.current = connectivity.status
    }
  }, [connectivity?.status])

  const getConnectivityStatus = useCallback(() => statusRef.current, [])
  const subscriptionExchange = useUrqlSubscriptionExchange(graphQLAccessToken, getConnectivityStatus)
  const handleError = useHandleGraphQLError(networkToast, getConnectivityStatus)

  const failureTracker = useRef<ConsecutiveFailureTracker>({})

  // biome-ignore lint/correctness/useExhaustiveDependencies: failureTracker is a ref, not a reactive dependency
  const client = useMemo(
    () =>
      createClient({
        url: getConfig().querierApiUrl,
        requestPolicy: 'cache-and-network',
        fetchOptions: () => {
          const headers: Record<string, string> = {}
          // if an authorization header passed when it's set to jwt auth, it'll
          // infer that the user is unauthenticated and set the hasura role to the HASURA_GRAPHQL_UNAUTHORIZED_ROLE
          // env var we set in our hasura docker container
          // https://hasura.io/docs/latest/auth/authentication/unauthenticated-access/
          if (!isNullish(graphQLAccessToken)) {
            headers.authorization = `Bearer ${graphQLAccessToken}`
          }
          return { headers }
        },
        exchanges: [
          metricsExchange,
          mapExchange({
            onOperation: operation => {
              if (navigator.onLine === false) {
                return {
                  ...operation,
                  context: {
                    ...operation.context,
                    requestPolicy: 'cache-only',
                  },
                }
              }

              if (getConnectivityStatus() !== 'healthy') {
                return {
                  ...operation,
                  context: {
                    ...operation.context,
                    requestPolicy: 'cache-first',
                  },
                }
              }

              return operation
            },
            onError: (error, operation) => {
              handleError(error, operation)
            },
            onResult: result => result,
          }),
          createOfflineCacheExchange(lastQueryResultsRef.current, getConnectivityStatus),
          failureLimitExchange(failureTracker.current),
          cacheExchange,
          /**
           * Note that this retryExchange controls how operations are retried in the urql Client I/O stream,
           * but it does NOT prevent operations from being re-triggered elsewhere in the application (like
           * a re-render of a component that's using useQuery). So even though we set maxNumberAttempts to 2
           * here, it does not necessarily mean a useQuery hook will stop fetching after 2 failures, it simply means
           * that the hook's operation will be re-tried up to 2 times with exponential backoff before the
           * result gets propagated back through the urql Client I/O stream
           */
          retryExchange({
            initialDelayMs: 1500,
            maxDelayMs: 5000,
            randomDelay: true,
            maxNumberAttempts: 2,
            retryIf: (err: CombinedError, operation: Operation<unknown, AnyVariables>) => {
              // Currently we only retry w/ exponential backoff on fetches that fail due to network error

              if (operation.kind !== 'query') {
                return false
              }

              if (err.networkError) {
                if (getConnectivityStatus() !== 'healthy' || navigator.onLine === false) {
                  return false
                }

                return true
              }

              return false
            },
          }),
          fetchExchange,
          subscriptionExchange,
        ],
      }),
    [graphQLAccessToken, handleError, failureTracker, subscriptionExchange],
  )

  return client
}

const UrqlWrapper = React.memo<React.PropsWithChildren>(({ children }) => {
  const graphQLAccessToken = useGraphQLAccessToken()

  const client = useUrqlClient(graphQLAccessToken)
  if (!graphQLAccessToken) {
    return FullScreenLoadingSpinner
  }

  return <Provider value={client}>{children}</Provider>
})

export const useUrqlAuthenticatedQueryClient = () => {
  const graphQLAccessToken = useGraphQLAccessToken()
  const client = useUrqlClient(graphQLAccessToken)
  return client
}

export const NonAuthUrqlWrapper = React.memo<React.PropsWithChildren>(({ children }) => {
  const client = useUrqlClient()

  return <Provider value={client}>{children}</Provider>
})

NonAuthUrqlWrapper.displayName = 'NonAuthUrqlWrapper'

export default UrqlWrapper
